/**
 * Qo'llanma — bu o'rnatma nima qiladi va u bilan qanday ishlanadi.
 *
 * Konsol ichida turadi, chunki bu yerda yozilgan narsalarning yarmi aynan shu
 * o'rnatmaga xos: portlar, katalog yo'llari, upstream'dan chetlashishlar. Repodagi
 * umumiy hujjat ularni bilmaydi, va boshqa oynada ochilgan hujjat o'qilmaydi.
 *
 * Ataylab statik: hech qanday so'rov yubormaydi. Platformaning o'zi ishlamay
 * qolganda ham ochilishi kerak bo'lgan yagona sahifa shu.
 */

import type { ReactNode } from 'react'

function Cmd({ children }: { children: string }) {
  return (
    <pre
      className="mono"
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 'var(--space-3)',
        overflowX: 'auto',
        whiteSpace: 'pre',
        margin: 'var(--space-2) 0',
      }}
    >
      {children}
    </pre>
  )
}

function Section({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: ReactNode
}) {
  return (
    <section aria-labelledby={id} style={{ marginBottom: 'var(--space-6)' }}>
      <h2 id={id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 'var(--space-2)' }}>
        {title}
      </h2>
      {children}
    </section>
  )
}

const SECTIONS: { name: string; path: string; what: string; when: string }[] = [
  {
    name: 'Overview',
    path: '/overview',
    what: 'Birinchi ekran: nimadir buzilganmi va nima odam aralashuvini kutyapti.',
    when: 'Har kuni ishni shu yerdan boshlang. Fleet paneli agentlar sonini, tasdiqlash kutayotganlarni va "jim qolgan" hostlarni ko\'rsatadi.',
  },
  {
    name: 'Dashboard',
    path: '/dashboard',
    what: 'O\'zingiz mahkamlagan (pin qilgan) hunt so\'rovlari — takrorlanadigan savollaringiz.',
    when: 'Hunt\'da foydali so\'rov topsangiz, uni shu yerga mahkamlang; keyingi safar qayta yozib o\'tirmaysiz.',
  },
  {
    name: 'Triage',
    path: '/queue',
    what: 'Aniqlangan hodisalar navbati. Har bir qator — bir finding, holati bilan.',
    when: 'Overview "nimadir bor" degandan keyin. Kundalik ishning asosiy joyi shu.',
  },
  {
    name: 'Hunt',
    path: '/hunt',
    what: 'Saqlangan hodisalar ustidan qidiruv. Vaqt oynasi va so\'rov narxi doim ko\'rinib turadi.',
    when: 'Qoida ushlamagan narsani qo\'lda qidirganda. Topganingizni darrov qoidaga aylantirsa bo\'ladi.',
  },
  {
    name: 'Rules',
    path: '/rules',
    what: 'Qoidalarning hayot sikli: qaysi biri qoralama, qaysi biri sinovdan o\'tgan, qaysi biri yoqilgan.',
    when: 'Yangi qoida qo\'shganda va qoida nega ishlamayotganini tekshirganda.',
  },
  {
    name: 'Response',
    path: '/response',
    what: 'Platforma estate\'ga nima qilgani va nima qilishga haqli ekani. Rad etilgan harakatlar ham ko\'rinadi.',
    when: 'Avtomatik javob (masalan IP bloklash) sozlangan bo\'lsa. Rad etish — bu nazorat ishlayotgani, xato emas.',
  },
  {
    name: 'Coverage',
    path: '/coverage',
    what: 'ATT&CK qoplamasi: nimani aniqlaymiz, nimani yo\'q. Uch holat — ishlayapti, qoplangan-u jim, va o\'chirilgan qoida bilan qoplangan.',
    when: 'Uchinchi holat uchun. U boshqa har bir ekranda "qoplama bor" bo\'lib ko\'rinadi, aslida esa yo\'q.',
  },
  {
    name: 'Inventory',
    path: '/inventory',
    what: 'Hostlarda nima o\'rnatilgan va nima o\'zgargan. Drift (o\'zgarish) birinchi, ro\'yxat ikkinchi.',
    when: '"Kecha nima o\'zgardi va buni kim so\'ragan edi" degan savolga. Agent har soatda inventarizatsiya yuboradi.',
  },
  {
    name: 'Vulnerabilities',
    path: '/vulnerabilities',
    what: 'Estate nimaga ochiq. Baholanmagan hostlar sonlardan YUQORIDA, nomi bilan ko\'rsatiladi.',
    when: 'Zaiflik hisoboti kerak bo\'lganda. "Tuzatish chiqmagan" alohida holat — bu ochiq patch bilan bir narsa emas.',
  },
  {
    name: 'Compliance',
    path: '/compliance',
    what: 'Muvofiqlik holati va foizning nimani QAMRAMAGANI.',
    when: 'Hisobot uchun. Foiz yonidagi "tekshirib bo\'lmadi" soni undan ajralmas — o\'qib bo\'lmagan fayl "toza" degani emas.',
  },
  {
    name: 'Reports',
    path: '/reports',
    what: 'Yaratilgan va rejalashtirilgan hisobotlar. Ular fayl sifatida saqlanadi.',
    when: 'Davriy hisobot kerak bo\'lganda. DIQQAT: hisobotni hech kim hech qayerga YUBORMAYDI — o\'zingiz kirib olasiz.',
  },
  {
    name: 'Administration',
    path: '/admin',
    what: 'Foydalanuvchilar, audit yozuvi va uning buzilmaganini tekshirish tugmasi.',
    when: 'Foydalanuvchi qo\'shganda va audit zanjirini tekshirganda. Tekshiruv natijasi nimani ISBOTLAMASLIGINI ham so\'z bilan aytadi.',
  },
]

export function GuidePage() {
  return (
    <div style={{ maxWidth: '54rem' }}>
      <h1>Qo'llanma</h1>
      <p className="muted">
        Bu sahifa shu o'rnatmaga xos. Umumiy hujjatlar repoda <span className="mono">docs/</span>{' '}
        ichida; bu serverning nostandart joylari{' '}
        <span className="mono">/opt/sentry/ops/README.md</span> da.
      </p>

      <Section id="what" title="Bu nima va nimaga javob beradi">
        <p>
          Sentry — hostlardagi loglarni o'qib, ularni bitta umumiy formatga (OCSF) keltirib,
          ustidan qoidalar yurgizadigan va natijani tekshiriladigan yozuvga aylantiradigan
          tizim. U uchta savolga javob beradi: <strong>hozir nimadir bo'lyaptimi</strong>,{' '}
          <strong>o'tmishda nima bo'lgan</strong>, va <strong>nimani umuman ko'rmayapmiz</strong>.
        </p>
        <p>
          Oxirgisi eng muhimi. Bu platformaning butun dizayni bitta xavfga qarshi qurilgan:
          monitoring tizimi hech narsa yig'masdan ham "yashil" bo'lib turishi mumkin. Shuning
          uchun u sonni bilmasa nol emas, "noma'lum" deb yozadi; o'qib bo'lmagan faylni
          "muammo yo'q" deb hisoblamaydi; va o'chirilgan qoidani qoplama sifatida ko'rsatmaydi.
        </p>

        <h3>Ma'lumot yo'li</h3>
        <Cmd>{`/var/log/auth.log ┐
journalctl (ssh)  ├─► idsagent ─► NATS ─► ingest ─┬─► ClickHouse   (hodisalar, xom)
journalctl (sudo) ┘   (host)     (navbat)  (qoidalar)└─► PostgreSQL   (findings, hash-zanjir)`}</Cmd>
        <p>
          <strong>Nega ikkita baza.</strong> ClickHouse hodisani hodisalar orasida saqlaydi —
          Hunt shuni so'raydi. PostgreSQL uni triage holati va hash-zanjiri bilan{' '}
          <em>alert</em> sifatida saqlaydi — analitik shu bilan ishlaydi, auditor shuni
          tekshiradi. Bittasiga yozib "bo'ldi" deyish — quvur sog'lom ko'rinib turib, natijasini
          hech kim ko'rmasligining yo'li.
        </p>
        <p>
          <strong>Agent aloqa uzilsa ham yig'averadi.</strong> Hodisalar
          <span className="mono"> /var/lib/sentry/queue</span> da lokal navbatga tushadi va aloqa
          tiklanganda yetkaziladi. Navbat holatini ko'rish:
        </p>
        <Cmd>sudo -u idsagent idsagent queue --dir /var/lib/sentry/queue</Cmd>
      </Section>

      <Section id="sections" title="Bo'limlar: har biri nima uchun">
        <table>
          <thead>
            <tr>
              <th scope="col">Bo'lim</th>
              <th scope="col">Vazifasi</th>
              <th scope="col">Qachon ochasiz</th>
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((s) => (
              <tr key={s.path}>
                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                  <strong>{s.name}</strong>
                  <br />
                  <span className="mono muted">{s.path}</span>
                </td>
                <td style={{ verticalAlign: 'top' }}>{s.what}</td>
                <td style={{ verticalAlign: 'top' }} className="muted">
                  {s.when}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="rules" title="Qoidalar qanday qo'shiladi">
        <div className="panel" style={{ marginBottom: 'var(--space-4)' }}>
          <p style={{ marginTop: 0 }}>
            <strong>Eng muhim ikki qoida — ularni chalkashtirmang:</strong>
          </p>
          <ul style={{ marginBottom: 0 }}>
            <li>
              Holati yozilmagan qoida <strong>ishlaydi</strong>. Holat yozuvi — bu qoidani
              <em> o'chirish</em> usuli, yoqish usuli emas. Sabab: aks holda unutilgan yozuv yoki
              muvaffaqiyatsiz migratsiya aniqlashni jimgina to'xtatib qo'yardi.
            </li>
            <li>
              Konsolda saqlangan qoida esa <strong>ishlamaydi</strong>. U qoralama bo'lib keladi va
              backtest'siz yoqilmaydi.
            </li>
          </ul>
        </div>

        <h3>Hayot sikli</h3>
        <Cmd>{`draft ──backtest (kamida 7 kun)──► tested ──yoqish──► enabled
                                                   │
                                        disabled ◄─┘      deprecated`}</Cmd>
        <p>
          Yetti kun — tasodifiy son emas: bir kunda sinalgan qoida dam olish kunini, zaxira
          oynasini va tungi batch ishini ko'rmagan bo'ladi, aynan o'sha uchtasi yolg'on
          ishlashlarning ko'pini beradi.
        </p>

        <h3>1-yo'l: Hunt'dan qoida yasash (tavsiya etiladi)</h3>
        <ol>
          <li>
            <strong>Hunt</strong> da qidiruvni yozing va kerakli natijani toping.
          </li>
          <li>
            <strong>Save as rule</strong> tugmasini bosing. Dialog Sigma hujjatini o'zi yasaydi va{' '}
            <em>har bir shart qaysi OCSF maydoniga tushganini</em> ko'rsatadi. Bu ko'rinishni
            o'tkazib yuborib bo'lmaydi — ataylab. <span className="mono">User</span> umumiy Linux
            qoidasida jarayon egasini, autentifikatsiya qoidasida esa kirayotgan hisobni bildiradi;
            noto'g'risini tanlagan qoida muammosiz kompilyatsiya bo'ladi va abadiy hech narsa
            ushlamaydi.
          </li>
          <li>Saqlang — qoralama hosil bo'ladi.</li>
        </ol>

        <h3>2-yo'l: Sigma faylini qo'lda yozish</h3>
        <p>
          Faylni <span className="mono">/opt/sentry/deploy/compose/rules-active/</span> ga qo'ying.
          Namuna sifatida o'sha katalogdagi <span className="mono">ssh_brute_force.yml</span> ni
          oching. Oynali (agregatsiyalovchi) qoida shunday ko'rinadi:
        </p>
        <Cmd>{`detection:
  failure:
    User|contains: ''
  timeframe: 5m
  condition: failure | count() by User > 5`}</Cmd>
        <p className="muted">
          <span className="mono">timeframe</span> siz "besh marta" degani hech nima emas — bir oyda
          besh marta bu parolini unutgan odam.
        </p>

        <h3>Backtest va yoqish</h3>
        <Cmd>{`# Qoidalarni saqlangan hodisalar ustida sinash
podman exec sentry-api-1 python manage.py backtest --tenant default --days 7

# Bitta qoidani sinash
podman exec sentry-api-1 python manage.py backtest --tenant default --rule-id <ID> --days 7`}</Cmd>
        <p>
          So'ng <strong>Rules</strong> bo'limida qoidani yoqing. Backtest'siz yoqish tugmasi
          ishlamaydi — bu taqiq ataylab, va Rules sahifasi backtest ustunini shuning uchun
          ko'rsatadi: sababi ko'rinmasa, rad etish o'zboshimchalikdek tuyuladi.
        </p>

        <h3>Oxirgi qadam: qoidani detektorga yetkazish</h3>
        <div className="panel" style={{ marginBottom: 'var(--space-3)' }}>
          <p style={{ margin: 0 }}>
            <strong>Buni o'tkazib yubormang.</strong> Konsolda yoqilgan qoida bazada yotadi;
            detektor esa qoidalarni <span className="mono">/rules-active</span> katalogidan o'qiydi.
            Eksport qilmaguningizcha qoida yoqilgan ko'rinadi va hech narsa aniqlamaydi.
          </p>
        </div>
        <Cmd>{`# Yoqilgan konsol qoidalarini detektor o'qiydigan katalogga yozish
podman exec sentry-api-1 python manage.py export_rules --tenant default --dest /rules-active

# Detektorga qayta o'qitish
sentryctl restart ingest

# Nechta qoida yuklanganini tekshirish
podman logs --since 2m sentry-ingest-1 | grep "rule(s) loaded"`}</Cmd>
        <p className="muted">
          Katalog host tomonda{' '}
          <span className="mono">/opt/sentry/deploy/compose/rules-active/</span>. Upstream o'zining
          builtin qoidalarini yangilasa, ular bu yerga avtomatik ko'chmaydi — yangilanishdan keyin
          qo'lda nusxalang.
        </p>
      </Section>

      <Section id="agent" title="Yangi host qo'shish">
        <div className="panel" style={{ marginBottom: 'var(--space-3)' }}>
          <p style={{ margin: 0 }}>
            <strong>Hozircha faqat shu server kuzatilyapti.</strong> NATS
            <span className="mono"> 127.0.0.1:47928</span> da, TLS'siz, parol bilan ishlaydi. Bu
            loopback uchun maqbul, boshqa server uchun emas. Ikkinchi hostni qo'shishdan oldin
            NATS'ga TLS berish SHART.
          </p>
        </div>
        <p>Enrolment uch chaqiruvdan iborat va agentda buning uchun subkomanda yo'q:</p>
        <ol>
          <li>Token yasash (konsol yoki API), 4 soatga.</li>
          <li>
            Hostda ECDSA P-256 kalit va CSR yaratish (RSA qabul qilinmaydi).
          </li>
          <li>
            <span className="mono">POST /api/v1/agents/enrol/</span> — agent "kutmoqda" holatiga
            tushadi.
          </li>
          <li>
            <strong>Tasdiqlash.</strong> Diqqat: hujjatda "konsolning fleet ko'rinishida
            tasdiqlang" deyilgan, lekin bu konsolda bunday tugma yo'q — Overview faqat kutayotganlar
            sonini ko'rsatadi. Tasdiqlash{' '}
            <span className="mono">POST /api/v1/agents/&#123;id&#125;/approve/</span> orqali.
          </li>
          <li>
            <span className="mono">POST /api/v1/agents/certificate/</span> — tasdiqlanmaguncha 202,
            tasdiqlangach 200 va sertifikat. Shuning uchun o'rnatuvchi skript uni shunchaki
            so'rab turaveradi.
          </li>
        </ol>
        <p className="muted">
          To'liq buyruqlar <span className="mono">/opt/sentry/docs/DEPLOYMENT.md</span> da, bu
          serverdagi agentning aniq sozlamalari esa{' '}
          <span className="mono">/etc/systemd/system/idsagent-collector.service</span> da.
        </p>
      </Section>

      <Section id="limits" title="Bu o'rnatma nima QILMAYDI">
        <ul>
          <li>
            <strong>Hech kimga xabar bermaydi.</strong> Email yo'q, webhook yo'q, SIEM yo'q,
            ticketing yo'q. Finding triage navbatida yotadi va kimdir konsolni ochmaguncha shunday
            qolaveradi. Hisobotlar ham fayl bo'lib saqlanadi, yuborilmaydi.
          </li>
          <li>
            <strong>O'zini o'zi kuzatmaydi.</strong> Agar Sentry to'xtasa, sizga hech narsa
            aytmaydi. Konteynerlar holatini boshqa vositangizga qo'shing.
          </li>
          <li>
            <strong>Zaxira nusxa olmaydi.</strong> ClickHouse volumeini alohida rejaga qo'shing.
          </li>
          <li>
            <strong>Baseline'lar darrov ishlamaydi</strong> — ular ogohlantirishdan oldin 30 ta
            kuzatuv va 7 kun o'rganish talab qiladi.
          </li>
          <li>
            <strong>Threat intelligence bo'sh</strong> — indikatorlar import qilinmagan
            (<span className="mono">manage.py import_indicators</span>).
          </li>
        </ul>
      </Section>

      <Section id="ops" title="Kundalik buyruqlar">
        <Cmd>{`sentryctl ps                 # konteynerlar holati
sentryctl logs -f api        # control plane loglari
sentryctl restart ingest     # qoidalarni qayta o'qitish
systemctl status idsagent-collector   # shu hostdagi agent

# Bog'liqliklar haqiqatan ishlayaptimi
cd /opt/sentry && set -a && . ./deploy/compose/.env && set +a \\
  && PYTHONPATH=/opt/sentry python3 -m control.healthcheck`}</Cmd>
        <p className="muted">
          Konsolga kirish yo'li buzilsa (nginx yoki sertifikat), zaxira yo'l bitta:{' '}
          <span className="mono">.env</span> da <span className="mono">SENTRY_INSECURE_HTTP=1</span>{' '}
          ni vaqtincha qaytarib <span className="mono">sentryctl up -d api</span>. Loopback orqali
          brauzerda ochish oddiy holatda ishlamaydi — Django uni https'ga yo'naltiradi, u yerda esa
          hech kim tinglamaydi.
        </p>
      </Section>
    </div>
  )
}
